import { useEffect, useId, useMemo, useRef, useState } from "react";
import { MdKeyboardArrowDown, MdSearch } from "react-icons/md";
import { brandInputClass } from "./fieldStyles";
import { FieldShell } from "./FieldShell";

function toOptionValue(value) {
  return value == null ? "" : String(value);
}

function normalizeText(value) {
  return toOptionValue(value).trim().toLocaleLowerCase();
}

export function InstitutionSearchSelect({
  label,
  name,
  value,
  institutionName = "",
  onChange,
  onBlur,
  onCreateOption,
  options = [],
  placeholder = "Buscá o agregá una institución",
  helper,
  error,
  disabled = false,
  required = false,
  className = "",
  showLabel = true,
}) {
  const rootRef = useRef(null);
  const inputId = useId();
  const listboxId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const selectedValue = toOptionValue(value);
  const selectedOption = useMemo(() => {
    return options.find((option) => toOptionValue(option.value) === selectedValue) ?? null;
  }, [options, selectedValue]);

  const initialQuery = useMemo(() => {
    if (selectedValue === "__new__") {
      return institutionName ?? "";
    }

    return selectedOption?.label ?? "";
  }, [institutionName, selectedOption, selectedValue]);

  const [query, setQuery] = useState(initialQuery);

  useEffect(() => {
    if (!selectedValue) {
      return;
    }

    if (selectedValue === "__new__") {
      if (query !== (institutionName ?? "")) {
        setQuery(institutionName ?? "");
      }
      return;
    }

    if (selectedOption && query !== selectedOption.label) {
      setQuery(selectedOption.label);
    }
  }, [institutionName, query, selectedOption, selectedValue]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalizeText(query);
    if (!normalizedQuery) {
      return [];
    }

    return options.filter((option) =>
      normalizeText(option.label).includes(normalizedQuery),
    );
  }, [options, query]);

  const exactMatch = useMemo(() => {
    const normalizedQuery = normalizeText(query);
    if (!normalizedQuery) {
      return null;
    }

    return (
      options.find((option) => normalizeText(option.label) === normalizedQuery) ?? null
    );
  }, [options, query]);

  const canCreate = !disabled && !isCreating && query.trim() && !exactMatch;
  const createLabel = query.trim()
    ? `Agregar "${query.trim()}"`
    : "Escribí para agregar una nueva institución";
  const noResults = filteredOptions.length === 0 && Boolean(query.trim()) && !canCreate;

  const commitExisting = (option) => {
    const nextValue = toOptionValue(option.value);

    onChange?.({
      target: {
        name,
        value: nextValue,
      },
    });

    onChange?.({
      target: {
        name: "institutionName",
        value: "",
      },
    });

    onBlur?.({
      target: {
        name,
      },
    });

    setQuery(option.label);
    setIsOpen(false);
  };

  const commitNew = async () => {
    const nextName = query.trim();
    if (!nextName) {
      return;
    }

    if (typeof onCreateOption !== "function") {
      return;
    }

    setIsCreating(true);

    try {
      const createdOption = await onCreateOption(nextName);
      if (createdOption) {
        const nextValue = toOptionValue(createdOption.value ?? createdOption.id);
        const nextLabel = createdOption.label ?? createdOption.name ?? nextName;

        onChange?.({
          target: {
            name,
            value: nextValue,
          },
        });

        onChange?.({
          target: {
            name: "institutionName",
            value: "",
          },
        });

        onBlur?.({
          target: {
            name,
          },
        });

        setQuery(nextLabel);
        setIsOpen(false);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleInputChange = (event) => {
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    setIsOpen(Boolean(nextQuery.trim()));

    if (selectedValue === "__new__") {
      onChange?.({
        target: {
          name: "institutionName",
          value: nextQuery,
        },
      });
      return;
    }

    if (selectedValue && selectedOption && nextQuery !== selectedOption.label) {
      onChange?.({
        target: {
          name,
          value: "",
        },
      });
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      if (exactMatch) {
        event.preventDefault();
        commitExisting(exactMatch);
        return;
      }

      if (canCreate) {
        event.preventDefault();
        void commitNew();
      }
    }
  };

  return (
    <FieldShell
      label={label}
      helper={helper}
      error={error}
      className={className}
      showLabel={showLabel}
    >
      <div ref={rootRef} className={`relative ${isOpen ? "z-40" : "z-0"}`}>
        <div className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-brand-primary/50">
          <MdSearch aria-hidden="true" className="h-5 w-5" />
        </div>
        <input
          id={inputId}
          name={name}
          value={query}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={isOpen}
          aria-invalid={Boolean(error)}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          onChange={handleInputChange}
          onFocus={() => {
            if (!disabled && (query.trim() || selectedValue)) {
              setIsOpen(true);
            }
          }}
          onBlur={(event) => {
            if (!rootRef?.current?.contains(event.relatedTarget)) {
              onBlur?.({
                target: {
                  name,
                },
              });
              setIsOpen(false);
            }
          }}
          onKeyDown={handleKeyDown}
          className={`${brandInputClass} pl-11 pr-11`}
        />
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-brand-primary/70">
          <MdKeyboardArrowDown
            aria-hidden="true"
            className={`h-5 w-5 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </div>

        {isOpen && !disabled ? (
          <div
            id={listboxId}
            role="listbox"
            aria-label={label || name}
            className="absolute z-50 mt-2 max-h-72 w-full overflow-auto rounded-2xl border border-brand-secondary/20 bg-white/95 p-1 shadow-[0_16px_36px_rgba(20,50,135,0.14)] backdrop-blur"
          >
            {noResults ? (
              <div className="px-4 py-3 text-sm text-slate-500">
                No encontramos coincidencias.
              </div>
            ) : null}

            {filteredOptions.map((option) => {
              const optionValue = toOptionValue(option.value);
              const isSelected = optionValue === selectedValue;

              return (
                <button
                  key={optionValue || option.label}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  disabled={option.disabled}
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  onClick={() => commitExisting(option)}
                  className={[
                    "flex w-full items-center px-2 py-2 text-left text-sm transition-colors",
                    isSelected
                      ? "font-semibold text-brand-primary"
                      : "text-slate-700 hover:text-brand-primary",
                    option.disabled ? "cursor-not-allowed opacity-50" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {option.label}
                </button>
              );
            })}

            <div className="my-1 border-t border-brand-secondary/10" />

            <button
              type="button"
              role="option"
              aria-selected={selectedValue === "__new__"}
              disabled={!canCreate}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() => {
                if (canCreate) {
                  void commitNew();
                }
              }}
              className={[
                "flex w-full items-center px-2 py-2 text-left text-sm transition-colors",
                canCreate
                  ? "text-brand-primary hover:text-brand-secondary"
                  : "cursor-not-allowed text-slate-400",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {isCreating ? "Creando..." : createLabel}
            </button>
          </div>
        ) : null}
      </div>
    </FieldShell>
  );
}
